import React, { useState, useRef } from 'react';
import IosCard from '../../components/iOSCard';
import { 
    UserIcon, 
    EnvelopeIcon, 
    KeyIcon, 
    PencilSquareIcon, 
    CheckCircleIcon,
    CameraIcon,
    ShieldCheckIcon,
    PencilIcon,
    BellAlertIcon,
    LockClosedIcon,
    XCircleIcon,
    BriefcaseIcon,
    ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const Profile = ({ user }) => {
    const fileInputRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('personal'); 
    
    // 🔔 حالات رسائل التأكيد
    const [statusModal, setStatusModal] = useState({ show: false, type: 'success', message: '' });

    const [formData, setFormData] = useState({
        name: user?.name || "الأستاذ علي ناصر",
        email: user?.email || "teacher@alnoor.com",
        password: "",
        newPassword: "",
        specialty: "أستاذ مادة الأحياء",
        bio: "خبير في تدريس مادة الأحياء للسادس العلمي بخبرة تمتد لأكثر من 10 سنوات.",
        image: user?.image || null 
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setFormData({ ...formData, image: reader.result });
            reader.readAsDataURL(file);
        }
    };

    const handleSave = () => {
        try {
            // هنا محاكاة للحفظ الناجح
            setIsEditing(false);
            showPopup('success', 'تم حفظ التعديلات بنجاح! ✨');
        } catch (error) {
            showPopup('error', 'عذراً، حدث خطأ أثناء الحفظ! ⚠️');
        }
    };

    const showPopup = (type, msg) => {
        setStatusModal({ show: true, type, message: msg });
        setTimeout(() => setStatusModal({ ...statusModal, show: false }), 3000);
    };

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="max-w-4xl mx-auto space-y-8 animate-in fade-in zoom-in-95 duration-700 pb-20 text-right" dir="rtl">
            
            {/* 🍏 1️⃣ هيدر البروفايل */}
            <div className="relative h-64 mb-20">
                <div className="absolute inset-0 h-48 bg-gradient-to-r from-[#007AFF] to-cyan-500 rounded-[48px] shadow-2xl overflow-hidden border-white/20 border z-0">
                    <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                </div>

                <div className="absolute -bottom-10 right-10 flex items-end gap-6 z-10">
                    <div className="relative">
                        <motion.div className="w-36 h-36 bg-white rounded-[40px] border-4 border-[#F2F2F7] overflow-hidden shadow-2xl flex items-center justify-center">
                            {formData.image ? (
                                <img src={formData.image} className="w-full h-full object-cover" alt="profile" />
                            ) : (
                                <div className="w-full h-full bg-blue-50 flex items-center justify-center text-blue-500 text-5xl font-black">
                                    {formData.name.charAt(0)}
                                </div>
                            )}
                        </motion.div>
                        <AnimatePresence>
                            {isEditing && (
                                <motion.button initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} onClick={() => fileInputRef.current.click()} className="absolute bottom-[-10px] left-[-10px] p-3 bg-[#007AFF] text-white rounded-2xl shadow-xl border-4 border-white z-20 active:scale-90 transition-all">
                                    <CameraIcon className="w-6 h-6" />
                                </motion.button>
                            )}
                        </AnimatePresence>
                        <input type="file" ref={fileInputRef} onChange={handleImageChange} className="hidden" accept="image/*" />
                    </div>
                    <div className="mb-14 text-white">
                        <h2 className="text-3xl font-black drop-shadow-lg">{formData.name}</h2>
                        <p className="text-sm font-bold opacity-90">{formData.specialty}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                    <div className="ios-glass p-4 rounded-[30px] border-white/40 shadow-sm space-y-2">
                        <button onClick={() => setActiveTab('personal')} className={`w-full flex items-center gap-3 p-3.5 rounded-2xl font-black text-sm transition-all ${activeTab === 'personal' ? 'bg-[#007AFF] text-white shadow-lg shadow-blue-500/30' : 'text-gray-600 hover:bg-white/50'}`}>
                            <UserIcon className="w-5 h-5" /> الحساب الشخصي
                        </button>
                        <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 p-3.5 rounded-2xl font-black text-sm transition-all ${activeTab === 'security' ? 'bg-[#007AFF] text-white shadow-lg shadow-blue-500/30' : 'text-gray-600 hover:bg-white/50'}`}>
                            <KeyIcon className="w-5 h-5" /> الأمان وكلمة السر
                        </button>
                        <button onClick={() => setActiveTab('notifications')} className={`w-full flex items-center gap-3 p-3.5 rounded-2xl font-black text-sm transition-all ${activeTab === 'notifications' ? 'bg-[#007AFF] text-white shadow-lg shadow-blue-500/30' : 'text-gray-600 hover:bg-white/50'}`}>
                            <BellAlertIcon className="w-5 h-5" /> التنبيهات
                        </button>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <AnimatePresence mode="wait">
                        {activeTab === 'personal' && (
                            <motion.div key="personal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <IosCard title="المعلومات الأساسية" icon={<PencilIcon />} color="blue">
                                    <div className="space-y-6 p-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الاسم الكامل</label>
                                                <input disabled={!isEditing} type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className={`w-full p-4 rounded-2xl transition-all font-bold text-gray-700 ${isEditing ? 'bg-white/60 border border-blue-200 shadow-inner' : 'bg-transparent border-transparent'}`} />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">التخصص</label>
                                                <input disabled={!isEditing} type="text" value={formData.specialty} onChange={(e) => setFormData({...formData, specialty: e.target.value})} className={`w-full p-4 rounded-2xl transition-all font-bold text-gray-700 ${isEditing ? 'bg-white/60 border border-blue-200 shadow-inner' : 'bg-transparent border-transparent'}`} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">البريد الإلكتروني</label>
                                            <input type="email" value={formData.email} className="w-full p-4 rounded-2xl bg-gray-50 text-gray-400 font-bold" readOnly />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">النبذة الشخصية</label>
                                            <textarea disabled={!isEditing} rows="3" value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} className={`w-full p-4 rounded-2xl transition-all font-bold text-gray-700 ${isEditing ? 'bg-white/60 border border-blue-200 shadow-inner' : 'bg-transparent border-transparent'}`} />
                                        </div>
                                        <button onClick={() => isEditing ? handleSave() : setIsEditing(true)} className={`w-full py-5 rounded-[24px] font-black text-sm transition-all ${isEditing ? 'bg-[#007AFF] text-white shadow-blue-500/40' : 'bg-white border border-gray-100 text-gray-600 shadow-xl'}`}>
                                            {isEditing ? 'حفظ التغييرات النهائية' : 'تعديل البيانات'}
                                        </button>
                                    </div>
                                </IosCard>
                            </motion.div>
                        )}

                        {/* بقية الأقسام (Security & Notifications) تبقى كما هي في الكود السابق */}
                        {activeTab === 'security' && (
                            <motion.div key="security" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <IosCard title="تغيير كلمة المرور" icon={<LockClosedIcon />} color="orange">
                                    <div className="space-y-6 p-2">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2">كلمة المرور الحالية</label>
                                            <input type="password" placeholder="••••••••" className="w-full p-4 rounded-2xl bg-white/60 border border-gray-100 outline-none font-bold shadow-inner" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase mr-2">كلمة المرور الجديدة</label>
                                            <input type="password" placeholder="اكتب كلمة المرور الجديدة" className="w-full p-4 rounded-2xl bg-white/60 border border-gray-100 outline-none font-bold shadow-inner" />
                                        </div>
                                        <button onClick={() => handleSave()} className="w-full py-5 bg-orange-500 text-white rounded-[24px] font-black text-sm shadow-xl shadow-orange-500/20 active:scale-95 transition-all">تحديث الأمان</button>
                                    </div>
                                </IosCard>
                            </motion.div>
                        )}
                        {/* ... قسم التنبيهات ... */}
                    </AnimatePresence>
                </div>
            </div>

            {/* ✅ 🏆 نافذة النجاح/الفشل المنبثقة (وسط الشاشة) */}
            <AnimatePresence>
                {statusModal.show && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 pointer-events-none">
                        <motion.div 
                            initial={{ scale: 0.8, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 20 }}
                            className="bg-white/80 backdrop-blur-2xl p-10 rounded-[50px] shadow-[0_20px_70px_rgba(0,0,0,0.15)] border border-white/50 flex flex-col items-center gap-4 text-center max-w-xs"
                        >
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${statusModal.type === 'success' ? 'bg-emerald-500 shadow-emerald-200' : 'bg-red-500 shadow-red-200'}`}>
                                {statusModal.type === 'success' ? (
                                    <CheckCircleIcon className="w-12 h-12 text-white" />
                                ) : (
                                    <XCircleIcon className="w-12 h-12 text-white" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-gray-800">{statusModal.type === 'success' ? 'رائع!' : 'عذراً!'}</h3>
                                <p className="text-gray-500 font-bold mt-1 text-sm">{statusModal.message}</p>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Profile;